import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  BookOpen,
  ChevronsDownUp,
  ChevronsUpDown,
  Loader2,
  Search,
  Trash2,
  X,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTasks } from '../hooks/useTasks';
import { useTimelineSessions } from '../hooks/useTimelineSessions';
import ConversationSessionCard, {
  getConversationSessionDate,
  getConversationSessionTitle,
} from '../components/session/ConversationSessionCard';
import { JournalDayView } from '../components/journal/JournalDayView';
import { TaskCard } from '../components/tasks/TaskCard';
import { DatePickerButton, toDateInputValue } from '../components/ui/DatePickerButton';
import { callProcessExtraction } from '../services/functions';
import { deleteSession, updateSession, type DeleteSessionResult } from '../services/sessions';
import { brandColors } from '@/design';
import type { Session, TaskPlan } from '../types';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
type TimelineTab = 'journal' | 'tasks' | 'conversations';
type PendingSessionDeletion = { sessionId: string; title: string; reason?: 'no_transcript' };

function startOfDay(d: Date): Date {
  const copy = new Date(d);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function getTaskDate(task: TaskPlan): Date {
  if (task.createdAt?.toDate) return task.createdAt.toDate();
  return new Date((task.createdAt as unknown as { seconds: number }).seconds * 1000);
}

function isActionItemComplete(item: TaskPlan['actionItems'][number]): boolean {
  if (typeof item.status === 'string') return item.status === 'done';
  return item.completed;
}

function isTaskListFullyCompleted(task: TaskPlan): boolean {
  if (task.status === 'completed') return true;
  if (task.actionItems.length === 0) return false;
  return task.actionItems.every(isActionItemComplete);
}

function generateDays(centerDate: Date, range: number): Date[] {
  const days: Date[] = [];
  for (let i = -range; i <= range; i++) {
    const d = new Date(centerDate);
    d.setDate(d.getDate() + i);
    d.setHours(0, 0, 0, 0);
    days.push(d);
  }
  return days;
}


export default function TimelinePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { allTasks, loading, toggle, addItem, deleteItem, reorder } = useTasks(user?.uid ?? null);
  const [activeTab, setActiveTab] = useState<TimelineTab>(() => {
    const tab = searchParams.get('tab');
    if (tab === 'tasks' || tab === 'conversations' || tab === 'journal') return tab;
    return 'journal';
  });
  const [hideCompletedLists, setHideCompletedLists] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchClosing, setSearchClosing] = useState(false);
  const [collapsedTaskIds, setCollapsedTaskIds] = useState<Set<string>>(new Set());

  const today = useMemo(() => startOfDay(new Date()), []);
  const [selectedDate, setSelectedDate] = useState(today);
  const scrollRef = useRef<HTMLDivElement>(null);
  const dayRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const searchCloseTimerRef = useRef<number | null>(null);

  const days = useMemo(() => generateDays(today, 14), [today]);
  const rangeStart = useMemo(() => startOfDay(days[0]), [days]);
  const rangeEndExclusive = useMemo(() => {
    const end = new Date(days[days.length - 1]);
    end.setDate(end.getDate() + 1);
    end.setHours(0, 0, 0, 0);
    return end;
  }, [days]);
  const { sessionsByDay, loading: sessionsLoading, refetch: refetchSessions } = useTimelineSessions(
    user?.uid ?? null,
    rangeStart,
    rangeEndExclusive
  );
  const [analyzingSessionId, setAnalyzingSessionId] = useState<string | null>(null);
  const [pendingDeletion, setPendingDeletion] = useState<PendingSessionDeletion | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const isErrorFeedback = Boolean(feedback?.startsWith('Error'));

  const scrollSelectedToCenter = useCallback((date: Date, behavior: ScrollBehavior = 'smooth') => {
    const key = startOfDay(date).toISOString();
    const el = dayRefs.current.get(key);
    const container = scrollRef.current;
    if (!el || !container) return;
    const containerRect = container.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    const scrollLeft = container.scrollLeft + (elRect.left - containerRect.left) - (containerRect.width / 2) + (elRect.width / 2);
    container.scrollTo({ left: scrollLeft, behavior });
  }, []);

  const tasksByDay = useMemo(() => {
    const map = new Map<string, TaskPlan[]>();
    for (const task of allTasks) {
      const d = startOfDay(getTaskDate(task));
      const key = d.toISOString();
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(task);
    }
    return map;
  }, [allTasks]);

  const selectedTasks = useMemo(() => {
    const tasks = tasksByDay.get(startOfDay(selectedDate).toISOString()) ?? [];
    return [...tasks].sort((a, b) => {
      const aComplete = a.status === 'completed' ? 1 : 0;
      const bComplete = b.status === 'completed' ? 1 : 0;
      return aComplete - bComplete;
    });
  }, [tasksByDay, selectedDate]);
  const trimmedSearchQuery = searchQuery.trim().toLowerCase();
  const visibleSelectedTasks = useMemo(() => (
    selectedTasks.filter((task) => {
      if (hideCompletedLists && isTaskListFullyCompleted(task)) return false;
      if (!trimmedSearchQuery) return true;
      if (task.title.toLowerCase().includes(trimmedSearchQuery)) return true;
      if (task.summary?.toLowerCase().includes(trimmedSearchQuery)) return true;
      if (task.actionItems.some((item) => item.text.toLowerCase().includes(trimmedSearchQuery))) return true;
      return false;
    })
  ), [hideCompletedLists, selectedTasks, trimmedSearchQuery]);
  const visibleTaskIds = useMemo(
    () => visibleSelectedTasks.map((task) => task.id),
    [visibleSelectedTasks]
  );
  const visibleTaskIdSet = useMemo(
    () => new Set(visibleTaskIds),
    [visibleTaskIds]
  );
  const allVisibleTasksExpanded = visibleTaskIds.length > 0
    && visibleTaskIds.every((taskId) => !collapsedTaskIds.has(taskId));

  const selectedSessions = useMemo(() => {
    const sessions = sessionsByDay.get(startOfDay(selectedDate).toISOString()) ?? [];
    return [...sessions].sort((a, b) => getConversationSessionDate(b).getTime() - getConversationSessionDate(a).getTime());
  }, [sessionsByDay, selectedDate]);

  useEffect(() => {
    const rawTab = searchParams.get('tab');
    const tabFromUrl: TimelineTab =
      rawTab === 'tasks' || rawTab === 'conversations' || rawTab === 'journal'
        ? rawTab
        : 'journal';
    setActiveTab((currentTab) => (currentTab === tabFromUrl ? currentTab : tabFromUrl));
  }, [searchParams]);

  useEffect(() => {
    if (!feedback) return;
    const timer = setTimeout(() => setFeedback(null), 4000);
    return () => clearTimeout(timer);
  }, [feedback]);

  useEffect(() => {
    return () => {
      if (searchCloseTimerRef.current) {
        window.clearTimeout(searchCloseTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    requestAnimationFrame(() => scrollSelectedToCenter(selectedDate, 'auto'));
  }, []);

  useEffect(() => {
    scrollSelectedToCenter(selectedDate);
  }, [selectedDate, scrollSelectedToCenter]);

  useEffect(() => {
    const onResize = () => scrollSelectedToCenter(selectedDate, 'instant');
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [selectedDate, scrollSelectedToCenter]);

  useEffect(() => {
    setCollapsedTaskIds((prev) => {
      const currentTaskIds = new Set(selectedTasks.map((task) => task.id));
      const next = new Set<string>();
      prev.forEach((taskId) => {
        if (currentTaskIds.has(taskId)) next.add(taskId);
      });
      return next;
    });
  }, [selectedTasks]);

  const SEARCH_CLOSE_ANIMATION_MS = 280;
  const searchVisible = activeTab === 'tasks';
  const searchExpanded = searchOpen || searchClosing;

  const openSearch = useCallback(() => {
    if (searchCloseTimerRef.current) {
      window.clearTimeout(searchCloseTimerRef.current);
      searchCloseTimerRef.current = null;
    }
    setSearchClosing(false);
    setSearchOpen(true);
  }, []);

  const closeSearch = useCallback(() => {
    if (!searchOpen && !searchClosing) return;
    if (searchCloseTimerRef.current) {
      window.clearTimeout(searchCloseTimerRef.current);
    }
    setSearchOpen(false);
    setSearchClosing(true);
    searchCloseTimerRef.current = window.setTimeout(() => {
      setSearchClosing(false);
      setSearchQuery('');
      searchCloseTimerRef.current = null;
    }, SEARCH_CLOSE_ANIMATION_MS);
  }, [searchClosing, searchOpen]);

  useEffect(() => {
    if (!searchOpen || !searchVisible) return undefined;
    const rafId = window.requestAnimationFrame(() => {
      searchInputRef.current?.focus();
    });
    return () => window.cancelAnimationFrame(rafId);
  }, [searchOpen, searchVisible]);

  useEffect(() => {
    if (activeTab === 'tasks') return;
    closeSearch();
  }, [activeTab, closeSearch]);

  const isToday = isSameDay(selectedDate, today);
  const selectedCount = activeTab === 'tasks'
    ? visibleSelectedTasks.length
    : activeTab === 'conversations'
      ? selectedSessions.length
      : 1;
  const selectedCountLabel = activeTab === 'tasks'
    ? selectedCount === 1 ? 'task' : 'tasks'
    : activeTab === 'conversations'
      ? selectedCount === 1 ? 'conversation' : 'conversations'
      : 'journal view';

  function handleTabChange(tab: TimelineTab) {
    setActiveTab(tab);
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    setSearchParams(next, { replace: true });
  }

  function toggleTaskCardCollapsed(taskId: string) {
    setCollapsedTaskIds((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) {
        next.delete(taskId);
      } else {
        next.add(taskId);
      }
      return next;
    });
  }

  function toggleExpandAllVisibleTasks() {
    setCollapsedTaskIds((prev) => {
      const next = new Set(prev);
      if (allVisibleTasksExpanded) {
        visibleTaskIds.forEach((taskId) => next.add(taskId));
      } else {
        visibleTaskIds.forEach((taskId) => next.delete(taskId));
      }
      return next;
    });
  }

  async function handleAnalyzeActiveSession(session: Session) {
    if (!user || session.status !== 'active') return;
    setAnalyzingSessionId(session.id);
    try {
      await callProcessExtraction(session.id, {});
      setFeedback(`Finished analysis for "${getConversationSessionTitle(session)}".`);
      refetchSessions();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to analyze conversation';
      const noTranscript = /no messages to extract from/i.test(message);
      if (noTranscript) {
        try {
          await updateSession(user.uid, session.id, { status: 'abandoned' });
        } catch {
          // Best effort — backend also marks this case abandoned.
        }
        setFeedback('No transcript was captured. This session was marked Abandoned.');
        setPendingDeletion({
          sessionId: session.id,
          title: getConversationSessionTitle(session),
          reason: 'no_transcript',
        });
      } else {
        setFeedback(`Error: ${message}`);
      }
      refetchSessions();
    } finally {
      setAnalyzingSessionId(null);
    }
  }

  function handleDateJump(picked: Date) {
    const t = picked.getTime();
    if (t >= days[0].getTime() && t <= days[days.length - 1].getTime()) {
      setSelectedDate(picked);
    } else {
      navigate(`/journal/${toDateInputValue(picked)}`);
    }
  }

  async function handleConfirmDelete() {
    if (!user || !pendingDeletion) return;
    setDeleting(true);
    try {
      const result: DeleteSessionResult = await deleteSession(user.uid, pendingDeletion.sessionId);
      const parts: string[] = [];
      if (result.nodesDeleted > 0) parts.push(`${result.nodesDeleted} belief${result.nodesDeleted !== 1 ? 's' : ''}`);
      if (result.edgesDeleted > 0) parts.push(`${result.edgesDeleted} connection${result.edgesDeleted !== 1 ? 's' : ''}`);
      if (result.motifsDeleted > 0) parts.push(`${result.motifsDeleted} pattern${result.motifsDeleted !== 1 ? 's' : ''}`);
      if (result.tasksDeleted > 0) parts.push(`${result.tasksDeleted} task${result.tasksDeleted !== 1 ? 's' : ''}`);
      if (result.dayFocusDeleted > 0) parts.push(`${result.dayFocusDeleted} day focus${result.dayFocusDeleted !== 1 ? 'es' : ''}`);
      const cleanupNote = parts.length > 0 ? ` Cleaned up ${parts.join(', ')}.` : '';
      setFeedback(`Deleted "${pendingDeletion.title}".${cleanupNote}`);
      refetchSessions();
    } catch (err) {
      setFeedback(`Error: ${err instanceof Error ? err.message : 'Failed to delete'}`);
    } finally {
      setDeleting(false);
      setPendingDeletion(null);
    }
  }

  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      <div
        className="sticky top-0 z-40"
        style={{
          background: 'var(--color-background)',
          borderBottom: '1px solid var(--color-border)',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        }}
      >
        <header className="px-5 pt-5 pb-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/')}
              className="p-2 -ml-2 rounded-xl text-[var(--color-text-secondary)] hover:bg-[var(--color-surface)] transition-colors"
            >
              <ArrowLeft size={20} />
            </button>
            <h1 className="text-xl font-bold text-[var(--color-text-primary)]">Timeline</h1>
          </div>
          <div className="flex items-center gap-2">
            <DatePickerButton
              variant="icon"
              date={selectedDate}
              onChange={handleDateJump}
              max={today}
            />
            {activeTab === 'tasks' && (
              <>
                <div
                  className={`flex items-center justify-end transition-all duration-300 ease-out ${
                    searchExpanded ? 'flex-1 max-w-sm md:max-w-lg lg:max-w-xl' : 'flex-none'
                  }`}
                >
                  <button
                    onClick={openSearch}
                    className={`inline-flex items-center justify-center h-9 rounded-lg transition-[width,opacity,transform,background-color] duration-300 ${
                      searchExpanded
                        ? 'w-0 opacity-0 scale-95 pointer-events-none'
                        : 'w-9 opacity-100 scale-100'
                    }`}
                    style={{
                      background: 'var(--color-surface)',
                      color: 'var(--color-text-secondary)',
                      border: '1px solid var(--color-border)',
                    }}
                    title="Search tasks"
                    aria-label="Search tasks"
                  >
                    <Search size={15} />
                  </button>
                  <div
                    className={`flex items-center gap-1.5 rounded-lg h-9 min-w-0 overflow-hidden transition-[max-width,opacity,transform,padding,border-color] duration-300 ease-out ${
                      searchExpanded
                        ? 'flex-1 max-w-sm md:max-w-lg lg:max-w-xl opacity-100 scale-100 px-2 border'
                        : 'max-w-0 opacity-0 scale-95 px-0 border border-transparent'
                    }`}
                    style={{
                      background: 'var(--color-surface)',
                      borderColor: searchExpanded ? 'var(--color-border)' : 'transparent',
                    }}
                  >
                    <Search size={13} style={{ color: 'var(--color-text-muted)', flexShrink: 0 }} />
                    <input
                      ref={searchInputRef}
                      type="text"
                      value={searchQuery}
                      onChange={(event) => setSearchQuery(event.target.value)}
                      placeholder="Search tasks..."
                      className="flex-1 bg-transparent text-xs text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] outline-none min-w-0"
                    />
                    <button
                      onClick={closeSearch}
                      className="shrink-0 hover:opacity-70 transition-opacity"
                      aria-label="Close search"
                    >
                      <X size={13} style={{ color: 'var(--color-text-muted)' }} />
                    </button>
                  </div>
                </div>

                <button
                  onClick={toggleExpandAllVisibleTasks}
                  className="p-2 rounded-lg transition-colors shrink-0"
                  style={{
                    background: 'var(--color-surface)',
                    color: 'var(--color-text-secondary)',
                    border: '1px solid var(--color-border)',
                  }}
                  title={allVisibleTasksExpanded ? 'Collapse all' : 'Expand all'}
                  aria-label={allVisibleTasksExpanded ? 'Collapse all' : 'Expand all'}
                >
                  {allVisibleTasksExpanded
                    ? <ChevronsDownUp size={15} />
                    : <ChevronsUpDown size={15} />
                  }
                </button>


                <button
                  onClick={() => setHideCompletedLists((prev) => !prev)}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium transition-colors text-center shrink-0"
                  style={{
                    background: hideCompletedLists ? `${brandColors.lightBlue}22` : 'var(--color-surface)',
                    color: hideCompletedLists ? brandColors.lightBlue : 'var(--color-text-secondary)',
                    border: `1px solid ${hideCompletedLists ? `${brandColors.lightBlue}66` : 'var(--color-border)'}`,
                  }}
                >
                  {hideCompletedLists ? 'Show Completed Lists' : 'Hide Completed Lists'}
                </button>
              </>
            )}
          </div>
        </header>
      </div>

      {/* Day selector strip */}
      <div
        ref={scrollRef}
        className="flex gap-1 px-5 pb-4 overflow-x-auto scrollbar-hide"
        style={{ scrollbarWidth: 'none' }}
      >
        {days.map((day) => {
          const isSelected = isSameDay(day, selectedDate);
          const dayTasks = tasksByDay.get(startOfDay(day).toISOString()) ?? [];
          const daySessions = sessionsByDay.get(startOfDay(day).toISOString()) ?? [];
          const hasTasks = dayTasks.length > 0;
          const hasSessions = daySessions.length > 0;
          const allDone = hasTasks && dayTasks.every((t) => t.status === 'completed');

          return (
            <button
              key={day.toISOString()}
              ref={(el) => {
                if (el) dayRefs.current.set(day.toISOString(), el);
              }}
              onClick={() => setSelectedDate(day)}
              className={`flex flex-col items-center justify-center shrink-0 w-12 h-16 rounded-xl transition-all ${
                isSelected
                  ? 'text-white'
                  : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-surface)]'
              }`}
              style={{
                background: isSelected ? brandColors.darkBlue : undefined,
              }}
            >
              <span className="text-[10px] uppercase leading-none opacity-70">
                {DAY_NAMES[day.getDay()]}
              </span>
              <span className="text-lg font-bold leading-none mt-1">{day.getDate()}</span>
              {(hasTasks || hasSessions) && (
                <div className="flex items-center gap-1 mt-1">
                  {hasTasks && (
                    <div
                      className="w-1.5 h-1.5 rounded-full"
                      style={{
                        background: allDone
                          ? 'var(--color-success)'
                          : isSelected
                            ? 'rgba(255,255,255,0.5)'
                            : brandColors.teal,
                      }}
                    />
                  )}
                  {hasSessions && (
                    <div
                      className="w-1.5 h-1.5 rounded-full"
                      style={{
                        background: isSelected ? 'rgba(255,255,255,0.85)' : brandColors.lightBlue,
                      }}
                    />
                  )}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Timeline tabs */}
      <div className="px-5 pb-3">
        <div className="flank-card rounded-xl p-1 grid grid-cols-3 gap-1 max-w-2xl mx-auto">
          <button
            onClick={() => handleTabChange('journal')}
            className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors flex items-center justify-center gap-1 ${
              activeTab === 'journal' ? 'text-white' : 'text-[var(--color-text-secondary)]'
            }`}
            style={{ background: activeTab === 'journal' ? brandColors.darkBlue : 'transparent' }}
          >
            <BookOpen size={14} />
            Journal
          </button>
          <button
            onClick={() => handleTabChange('tasks')}
            className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
              activeTab === 'tasks' ? 'text-white' : 'text-[var(--color-text-secondary)]'
            }`}
            style={{ background: activeTab === 'tasks' ? brandColors.darkBlue : 'transparent' }}
          >
            Tasks
          </button>
          <button
            onClick={() => handleTabChange('conversations')}
            className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
              activeTab === 'conversations' ? 'text-white' : 'text-[var(--color-text-secondary)]'
            }`}
            style={{ background: activeTab === 'conversations' ? brandColors.darkBlue : 'transparent' }}
          >
            Conversations
          </button>
        </div>
      </div>

      {/* Selected day header */}
      <div className="px-5 pb-3">
        <p className="text-xs font-semibold text-[var(--color-text-secondary)] uppercase tracking-wider">
          {isToday ? 'Today' : `${DAY_NAMES[selectedDate.getDay()]} ${selectedDate.getDate()} ${MONTHS[selectedDate.getMonth()]}`}
          {selectedCount > 0 && (
            <span className="ml-2 normal-case tracking-normal font-normal text-[var(--color-text-muted)]">
              {selectedCount} {selectedCountLabel}
            </span>
          )}
        </p>
      </div>

      {/* Task list for selected day */}
      <div className="px-5 pb-10 max-w-2xl mx-auto flex flex-col">
        {((activeTab === 'tasks' && loading) || (activeTab === 'conversations' && sessionsLoading)) && (
          <div className="py-12 flex justify-center">
            <div className="w-6 h-6 rounded-full animate-pulse" style={{ background: 'var(--color-surface)' }} />
          </div>
        )}

        {activeTab === 'journal' && (
          <JournalDayView userId={user?.uid ?? null} date={selectedDate} />
        )}

        {!loading && activeTab === 'tasks' && visibleSelectedTasks.length === 0 && (
          <div className="py-12 text-center">
            <p className="text-sm text-[var(--color-text-muted)]">
              {selectedTasks.length > 0 && trimmedSearchQuery
                ? `No tasks match "${searchQuery.trim()}"`
                : selectedTasks.length > 0 && hideCompletedLists
                  ? 'Completed task lists are hidden'
                  : isToday
                    ? 'No tasks for today'
                    : 'No tasks on this day'}
            </p>
          </div>
        )}

        {!sessionsLoading && activeTab === 'conversations' && selectedSessions.length === 0 && (
          <div className="py-12 text-center">
            <p className="text-sm text-[var(--color-text-muted)]">
              {isToday ? 'No conversations today' : 'No conversations on this day'}
            </p>
          </div>
        )}

        {activeTab === 'tasks' && selectedTasks.map((task) => {
          const isVisible = visibleTaskIdSet.has(task.id);
          return (
            <div
              key={task.id}
              className="overflow-hidden"
              style={{
                display: 'grid',
                gridTemplateRows: isVisible ? '1fr' : '0fr',
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0) scaleY(1)' : 'translateY(-6px) scaleY(0.96)',
                transformOrigin: 'top',
                marginBottom: isVisible ? 12 : 0,
                pointerEvents: isVisible ? 'auto' : 'none',
                transition: [
                  'grid-template-rows 700ms cubic-bezier(0.22,1,0.36,1)',
                  'opacity 460ms ease',
                  'transform 700ms cubic-bezier(0.22,1,0.36,1)',
                  'margin-bottom 700ms cubic-bezier(0.22,1,0.36,1)',
                ].join(', '),
              }}
            >
              <div className="min-h-0 overflow-hidden">
                <TaskCard
                  task={task}
                  onToggle={toggle}
                  onAddItem={addItem}
                  onDeleteItem={deleteItem}
                  onReorder={reorder}
                  collapsed={collapsedTaskIds.has(task.id)}
                  onToggleCollapse={() => toggleTaskCardCollapsed(task.id)}
                />
              </div>
            </div>
          );
        })}

        {activeTab === 'conversations' && selectedSessions.map((session) => (
          <div key={session.id} style={{ marginBottom: 12 }}>
            <ConversationSessionCard
              session={session}
              onOpen={(sessionId) => navigate(`/conversations/${sessionId}`)}
              onAnalyze={() => handleAnalyzeActiveSession(session)}
              isAnalyzing={analyzingSessionId === session.id}
              onDelete={() => setPendingDeletion({
                sessionId: session.id,
                title: getConversationSessionTitle(session),
              })}
            />
          </div>
        ))}

        {feedback && (
          <div
            className="fixed bottom-6 left-1/2 -translate-x-1/2 max-w-sm px-4 py-3 rounded-xl text-sm shadow-lg z-50"
            style={{
              background: isErrorFeedback
                ? 'var(--color-error)'
                : 'var(--color-success-subtle)',
              color: isErrorFeedback
                ? 'white'
                : 'var(--color-success)',
              border: `1px solid ${isErrorFeedback ? 'var(--color-error)' : 'var(--color-success)'}`,
              boxShadow: isErrorFeedback
                ? '0 10px 28px rgba(0, 0, 0, 0.35)'
                : '0 10px 28px rgba(0, 0, 0, 0.28)',
            }}
            onClick={() => setFeedback(null)}
          >
            {feedback}
          </div>
        )}
      </div>

      {pendingDeletion && (
        <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50 p-4">
          <div
            className="w-full max-w-sm rounded-2xl p-6 flex flex-col gap-4"
            style={{ background: 'var(--color-background)' }}
          >
            <div>
              <h3 className="font-semibold text-[var(--color-text-primary)]">
                {pendingDeletion.reason === 'no_transcript' ? 'Delete empty conversation?' : 'Delete conversation?'}
              </h3>
              <p className="text-sm text-[var(--color-text-secondary)] mt-1">
                {pendingDeletion.reason === 'no_transcript'
                  ? `No transcript was captured for "${pendingDeletion.title}". Delete this empty conversation now?`
                  : `Delete "${pendingDeletion.title}" and undo its contributions to your belief system?`}
              </p>
              <p className="text-xs text-[var(--color-text-muted)] mt-2">
                {pendingDeletion.reason === 'no_transcript'
                  ? 'Keeping it is safe too: it will remain marked as abandoned.'
                  : 'This removes beliefs, connections, and patterns that were only established in this conversation. Data reinforced by other sessions will be preserved.'}
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setPendingDeletion(null)}
                disabled={deleting}
                className="flex-1 py-2.5 rounded-xl text-sm font-medium transition-colors"
                style={{
                  background: 'var(--color-surface)',
                  color: 'var(--color-text-secondary)',
                  border: '1px solid var(--color-border)',
                }}
              >
                {pendingDeletion.reason === 'no_transcript' ? 'Keep as abandoned' : 'Cancel'}
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={deleting}
                className="flex-1 py-2.5 rounded-xl text-sm font-medium transition-colors flex items-center justify-center gap-2"
                style={{
                  background: 'var(--color-error)',
                  color: 'white',
                  opacity: deleting ? 0.7 : 1,
                }}
              >
                {deleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

